import { useMemo, useState } from "react";
import type { HousingRtmsScopeId } from "../data/adapters/housingRtms";
import { totalTransactionsSeries } from "../data/housingChartSeries";
import type { HousingPolicyBundle } from "../data/housingPolicyRegimes";
import { monthlyAverageSeries } from "../data/housingTransmissionSeries";
import { resolveExpectedInflationSeries, type LegacyMortgageExpectedInflationHorizon, type LegacyMortgageExpectedInflationView, type LegacyMortgageSnapshot, type LegacyMortgageStressBundle } from "../data/legacyMortgageStress";
import type { IndicatorSeries, RangeKey, Unit } from "../types";
import TimeSeriesChart from "./TimeSeriesChart";

const SCOPE_LABELS: Partial<Record<HousingRtmsScopeId, string>> = {
  seoul: "서울",
  gangnam3: "강남3구",
  prime4: "강남3구+용산",
  seoul_non_gangnam: "서울 비강남3구",
  capital: "수도권"
};

const SCOPE_ORDER: HousingRtmsScopeId[] = ["seoul", "gangnam3", "prime4", "seoul_non_gangnam", "capital"];
const INFLATION_VIEW_ORDER: LegacyMortgageExpectedInflationView[] = ["COMPARE", "1Y", "3Y", "5Y"];
const INFLATION_VIEW_LABEL: Record<LegacyMortgageExpectedInflationView, string> = { COMPARE: "비교", "1Y": "1Y", "3Y": "3Y", "5Y": "5Y" };

type DetailRequest = (spec: { title: string; description: string; series: IndicatorSeries[]; referenceZero?: boolean }) => void;

function latestSeriesValue(series: IndicatorSeries | null) {
  if (!series) return { month: null as string | null, value: null as number | null };
  const valid = series.observations
    .filter((o): o is typeof o & { value: number } => o.value != null && Number.isFinite(o.value))
    .sort((a, b) => a.date.localeCompare(b.date));
  const latest = valid.at(-1);
  return { month: latest?.date.slice(0, 7) ?? null, value: latest?.value ?? null };
}

function fmtValue(value: number | null, unit: Unit, digits = 2) {
  if (value == null || !Number.isFinite(value)) return "N/A";
  if (unit === "%") return `${value.toFixed(digits)}%`;
  if (unit === "pp") return `${value.toFixed(digits)}%p`;
  if (unit === "bp") return `${value.toFixed(1)}bp`;
  if (unit === "trillion_krw") return `${value.toFixed(digits)}조원`;
  if (unit === "eok_krw") return `${value.toFixed(digits)}억원`;
  if (unit === "index") return value.toFixed(digits);
  return value.toFixed(digits);
}

function fmtChange(value: number | null, unit: Unit) {
  if (value == null || !Number.isFinite(value)) return "N/A";
  const sign = value > 0 ? "+" : "";
  if (unit === "%" || unit === "pp") return `${sign}${value.toFixed(2)}%p`;
  if (unit === "trillion_krw") return `${sign}${value.toFixed(2)}조원`;
  if (unit === "eok_krw") return `${sign}${value.toFixed(2)}억원`;
  return `${sign}${value.toFixed(2)}`;
}

function MetricCard({ label, snapshot, unit, note }: { label: string; snapshot: LegacyMortgageSnapshot; unit: Unit; note: string }) {
  return (
    <article className="legacy-mortgage-card">
      <div className="legacy-mortgage-card__top">
        <span>{label}</span>
        <b className={`data-pill ${snapshot.availability === "DATA" ? "data-pill--ok" : "data-pill--warn"}`}>{snapshot.availability}</b>
      </div>
      <strong>{fmtValue(snapshot.current, unit)}</strong>
      <div className="legacy-mortgage-card__changes">
        <span>1M {fmtChange(snapshot.change1m, unit)}</span>
        <span>6M {fmtChange(snapshot.change6m, unit)}</span>
      </div>
      <small>{snapshot.latestMonth ?? "관측월 없음"} · {note}</small>
    </article>
  );
}

export default function LegacyMortgageStressSection({
  legacy,
  housing,
  fundingRate,
  benchmarkRate,
  range,
  onOpenDetail
}: {
  legacy: LegacyMortgageStressBundle;
  housing: HousingPolicyBundle;
  fundingRate: IndicatorSeries;
  benchmarkRate: IndicatorSeries;
  range: RangeKey;
  onOpenDetail: DetailRequest;
}) {
  const [scope, setScope] = useState<HousingRtmsScopeId>("seoul");
  const [inflationView, setInflationView] = useState<LegacyMortgageExpectedInflationView>("COMPARE");

  const benchmarkMonthly = useMemo(() => monthlyAverageSeries(benchmarkRate, { id: "housing_ktb5_monthly", name: "국고채 5년 월평균", shortName: "KTB5Y 월평균" }), [benchmarkRate]);
  const fundingMonthly = useMemo(() => monthlyAverageSeries(fundingRate, { id: "housing_funding_monthly", name: "금융채 신규조달금리 월평균", shortName: "금융채 신규조달" }), [fundingRate]);
  const rateSeries = useMemo(() => [benchmarkMonthly, fundingMonthly, legacy.inputs.newMortgageRate, legacy.inputs.existingMortgageCost], [benchmarkMonthly, fundingMonthly, legacy.inputs.newMortgageRate, legacy.inputs.existingMortgageCost]);

  const rtms = housing.transactionModule.rtms;
  const transactionSeries = useMemo(() => totalTransactionsSeries({
    rows: rtms?.scopes[scope] ?? [],
    scope,
    scopeLabel: SCOPE_LABELS[scope] ?? scope,
    status: rtms?.status ?? "not_collected",
    generatedAt: rtms?.generatedAt,
    dataset: "아파트 매매 실거래 월별 집계"
  }), [rtms, scope]);

  const existingGate = legacy.gates.find((gate) => gate.id === "existingMortgageCost");
  const repricingGate = legacy.gates.find((gate) => gate.id === "repricingExposure");
  const balanceGate = legacy.gates.find((gate) => gate.id === "mortgageBalance");
  const housingOutlookGate = legacy.gates.find((gate) => gate.id === "housingPriceOutlook");
  const inflationGate = legacy.gates.find((gate) => gate.id === "expectedInflation");

  const inflationSeries = useMemo(() => ({
    "1Y": resolveExpectedInflationSeries(legacy.expectedInflationCandidates, "1Y"),
    "3Y": resolveExpectedInflationSeries(legacy.expectedInflationCandidates, "3Y"),
    "5Y": resolveExpectedInflationSeries(legacy.expectedInflationCandidates, "5Y")
  } satisfies Record<LegacyMortgageExpectedInflationHorizon, IndicatorSeries | null>), [legacy.expectedInflationCandidates]);
  const selectedInflation = inflationView === "COMPARE" ? null : inflationSeries[inflationView];
  const selectedInflationLatest = latestSeriesValue(selectedInflation);
  const visibleInflationSeries = useMemo(() => {
    if (inflationView === "COMPARE") return [inflationSeries["1Y"], inflationSeries["3Y"], inflationSeries["5Y"]].filter((item): item is IndicatorSeries => Boolean(item));
    return inflationSeries[inflationView] ? [inflationSeries[inflationView]!] : [legacy.inputs.expectedInflation];
  }, [inflationSeries, inflationView, legacy.inputs.expectedInflation]);

  return (
    <section className="legacy-mortgage-section">
      <header className="housing-h2-header">
        <div>
          <p className="kicker">H1B · LEGACY MORTGAGE STRESS</p>
          <h4>신규 leverage flow와 기존 mortgage stock을 분리</h4>
          <p>기존차주의 잔액금리·stock 재가격 노출은 exact metadata가 검증될 때만 연결합니다. 유사 계열로 숫자를 채우지 않습니다.</p>
        </div>
        <div className="housing-h2-status">
          <span className={`data-pill ${legacy.status.state === "ready" ? "data-pill--ok" : "data-pill--warn"}`}>{legacy.status.state.toUpperCase()}</span>
          <span>Repricing {legacy.status.repricingLabel}</span>
          <span>Cash-flow score {legacy.status.cashFlowStressLabel}</span>
          <span>Auto classification {legacy.status.classification}</span>
        </div>
      </header>

      <div className="legacy-mortgage-card-grid">
        <MetricCard label="기존차주 금리" snapshot={legacy.snapshots.existingMortgageCost} unit="%" note="잔액기준 주담대 exact series" />
        <MetricCard label="신규 주담대금리" snapshot={legacy.snapshots.newMortgageRate} unit="%" note="신규취급액 기준" />
        <MetricCard label="Lock-in Spread" snapshot={legacy.snapshots.lockInSpread} unit="pp" note="신규 − 기존 · 매도확률 아님" />
        <MetricCard label="Legacy Repricing" snapshot={legacy.snapshots.repricingPressure} unit="pp" note="Δ기존금리 × stock exposure" />
      </div>

      {legacy.status.state !== "ready" && (
        <div className="legacy-mortgage-gates">
          {[existingGate, repricingGate, balanceGate, housingOutlookGate, inflationGate].filter(Boolean).map((gate) => (
            <article key={gate!.id}>
              <span>{gate!.state === "ready" ? "READY" : gate!.state === "pending_decision" ? "DECISION" : "GATE"}</span>
              <strong>{gate!.label}</strong>
              <p>{gate!.reason}</p>
            </article>
          ))}
        </div>
      )}

      <section className="housing-h4-section">
        <TimeSeriesChart
          title="시장금리 → 조달 → 신규 주담대 → 기존차주 금리"
          subtitle="모든 계열을 월축으로 맞춰 flow와 stock의 전달속도를 비교합니다."
          series={rateSeries}
          range={range}
          height={260}
          onOpenDetail={() => onOpenDetail({
            title: "시장금리 → 조달 → 신규 주담대 → 기존차주 금리",
            description: "KTB5Y는 유효 일별 관측의 월평균, 금융채·주담대는 공식 월별 계열입니다. 상세보기에서 기간·변화량·Z-score·percentile·드래그 확대를 사용할 수 있습니다.",
            series: rateSeries
          })}
        />
      </section>

      <section className="housing-h4-section">
        <header className="housing-h4-header legacy-mortgage-lockin-header">
          <div>
            <h4>Lock-in Spread × 거래량</h4>
            <p>동일 월축에서 금융비용 격차와 거래 위축이 함께 나타나는지 관찰합니다. 상관을 인과나 매도확률로 바꾸지 않습니다.</p>
          </div>
          <div className="housing-profile-switch" role="group" aria-label="H1B 거래 지역범위">
            {SCOPE_ORDER.map((id) => <button key={id} type="button" className={scope === id ? "is-active" : ""} onClick={() => setScope(id)}>{SCOPE_LABELS[id]}</button>)}
          </div>
        </header>
        <div className="housing-h4-grid">
          <TimeSeriesChart
            title="Lock-in Spread"
            subtitle="신규 주담대금리 − 기존차주 잔액 주담대금리"
            series={[legacy.lockInSpread]}
            range={range}
            referenceZero
            height={230}
            syncId="housing-h1b-lockin"
            onOpenDetail={() => onOpenDetail({ title: "Lock-in Spread", description: "신규와 기존 주담대 금리의 월별 exact-match 차이입니다. 매도확률이나 가격충격으로 변환하지 않습니다.", series: [legacy.lockInSpread], referenceZero: true })}
          />
          <TimeSeriesChart
            title={`${SCOPE_LABELS[scope]} 전체 거래건수`}
            subtitle="선택지역 가격구간별 아파트 거래건수의 월별 합계"
            series={[transactionSeries]}
            range={range}
            height={230}
            syncId="housing-h1b-lockin"
            onOpenDetail={() => onOpenDetail({ title: `${SCOPE_LABELS[scope]} 전체 거래건수`, description: transactionSeries.source.note ?? "", series: [transactionSeries] })}
          />
        </div>
      </section>

      <section className="housing-h4-section">
        <header className="housing-h4-header legacy-mortgage-lockin-header">
          <div>
            <h4>기대인플레이션 horizon</h4>
            <p>1Y·3Y·5Y exact 원계열을 모두 보존합니다. 비교 모드는 세 계열을 동시에 표시하며 특정 horizon을 고정하지 않습니다.</p>
          </div>
          <div className="housing-profile-switch" role="group" aria-label="H1B 기대인플레이션 horizon">
            {INFLATION_VIEW_ORDER.map((view) => <button key={view} type="button" className={inflationView === view ? "is-active" : ""} onClick={() => setInflationView(view)}>{INFLATION_VIEW_LABEL[view]}</button>)}
          </div>
        </header>
        <div className="legacy-mortgage-inflation-selection">
          <strong>{inflationView === "COMPARE" ? "1Y / 3Y / 5Y 비교" : `${inflationView} 선택`}</strong>
          <span>{inflationView === "COMPARE" ? "고정 horizon 없음" : `${selectedInflationLatest.month ?? "관측월 없음"} · ${fmtValue(selectedInflationLatest.value, "%")}`}</span>
        </div>
        <div className="housing-h4-grid">
          <TimeSeriesChart
            title="주택가격전망 CSI"
            subtitle="한국은행 소비자동향조사 · 실제 가격지수와 구분"
            series={[legacy.inputs.housingPriceOutlook]}
            range={range}
            height={240}
            onOpenDetail={() => onOpenDetail({
              title: "주택가격전망 CSI",
              description: "한국은행 소비자동향조사의 주택가격전망 CSI exact series입니다. 실제 주택가격 수준·상승률이 아니며 상세보기에서 기간·변화량·Z-score·percentile·드래그 확대를 사용할 수 있습니다.",
              series: [legacy.inputs.housingPriceOutlook]
            })}
          />
          <TimeSeriesChart
            title="가계 기대인플레이션"
            subtitle="COMPARE에서는 세 horizon을 동시에 표시합니다."
            series={visibleInflationSeries}
            range={range}
            height={240}
            onOpenDetail={() => onOpenDetail({ title: "가계 기대인플레이션 1Y / 3Y / 5Y", description: "세 exact 원계열을 모두 보존하며 상세보기에서 계열별 토글과 기간/변환 분석을 할 수 있습니다.", series: [inflationSeries["1Y"], inflationSeries["3Y"], inflationSeries["5Y"]].filter((item): item is IndicatorSeries => Boolean(item)) })}
          />
        </div>
      </section>

      <section className="housing-h4-section">
        <header className="housing-h4-header">
          <h4>Legacy Repricing / Aggregate Interest Exposure</h4>
          <p>stock exposure와 모집단이 일치하는 주담대 잔액이 확보되면 자동 계산됩니다. 값이 없을 때는 0이 아니라 N/A입니다.</p>
        </header>
        <div className="housing-h4-grid">
          <TimeSeriesChart
            title="Legacy Repricing Pressure"
            subtitle="Δ기존 주담대금리 × stock 변동금리 노출비중"
            series={[legacy.repricingPressure]}
            range={range}
            referenceZero
            height={220}
            onOpenDetail={() => onOpenDetail({ title: "Legacy Repricing Pressure", description: legacy.repricingPressure.source.note ?? "", series: [legacy.repricingPressure], referenceZero: true })}
          />
          <div className="housing-h4-panel legacy-mortgage-na-panel">
            <strong>Cash-flow Stress / 자동 상태분류</strong>
            <p>현 단계에서는 N/A입니다.</p>
            <small>{legacy.status.classificationReason}</small>
          </div>
        </div>
      </section>

      <div className="housing-interpretation-grid">
        <article><span className="housing-evidence housing-evidence--observed">Observed</span><strong>{legacy.interpretation.observed}</strong><p>데이터가 없는 입력은 0으로 취급하지 않고 별도 Gate로 남깁니다.</p></article>
        <article><span className="housing-evidence housing-evidence--mechanism">Mechanism</span><strong>신규 flow와 기존 stock의 금리전달 속도는 다를 수 있습니다.</strong><p>{legacy.interpretation.mechanism}</p></article>
        <article><span className="housing-evidence housing-evidence--inference">Inference</span><strong>금리만으로 매도·가격충격을 자동 판정하지 않습니다.</strong><p>{legacy.interpretation.inference}</p></article>
      </div>
    </section>
  );
}
